import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

/*
 * TeamActivityCard
 * -----------------
 * Compact business-mode dashboard widget shown above the Daily Brief on
 * the home page when:
 *   - the active workspace is an Organization, AND
 *   - the current user is an Owner or Admin of that org (the endpoint
 *     enforces this server-side; the component additionally hides itself
 *     if the API returns 403 so members don't see an empty error state).
 *
 * The widget calls `GET /api/organizations/{org_id}/activity?window=…`
 * and lets the user toggle between Today / This week / This month. Rows
 * are sorted by total activity so the busiest teammate is at the top.
 *
 * Built deliberately small — three numeric columns and an avatar — so
 * it loads in well under 100 ms and never crowds the main task feed.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { key: 'today', label: 'Today' },
  { key: 'week',  label: 'This week' },
  { key: 'month', label: 'This month' },
];

const initialsOf = (name = '?') => {
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const TeamActivityCard = ({ org }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [window, setWindow] = useState('week');
  const [loading, setLoading] = useState(false);
  const [hidden, setHidden] = useState(false); // member without rights → 403 → hide forever

  const fetchActivity = useCallback(async (w) => {
    if (!org?.id || hidden) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/organizations/${org.id}/activity`,
        {
          params: { window: w },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.status === 401) {
        // Caller isn't allowed (member) — silently hide. The card is a
        // bonus for admins, not core functionality.
        setHidden(true);
      }
    } finally {
      setLoading(false);
    }
  }, [org?.id, hidden]);

  useEffect(() => {
    fetchActivity(window);
  }, [window, fetchActivity]);

  // Don't render anything until we know we're allowed AND have data.
  if (hidden || !org) return null;

  const totals = data?.totals || { created_count: 0, completed_count: 0, overdue_count: 0 };
  const rows = data?.rows || [];
  const hasAnyActivity = totals.created_count + totals.completed_count > 0;

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5 mb-4"
      data-testid="team-activity-card"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            📊 Team activity
          </h2>
          <p className="text-xs text-gray-500 truncate">
            {hasAnyActivity
              ? `Across ${rows.length} teammate${rows.length === 1 ? '' : 's'}`
              : 'Once your team creates and completes tasks, the rollup will appear here.'}
          </p>
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWindow(w.key)}
              data-testid={`team-activity-window-${w.key}`}
              className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                window === w.key
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Column headings — hidden on mobile to save space. */}
      <div className="hidden sm:grid grid-cols-[1fr,auto,auto,auto] gap-4 text-xs text-gray-400 uppercase tracking-wide pb-2 border-b border-gray-100">
        <div>Teammate</div>
        <div className="text-right w-16">✅ Done</div>
        <div className="text-right w-16">📝 Created</div>
        <div className="text-right w-16">⏰ Overdue</div>
      </div>

      <div className="divide-y divide-gray-100">
        {loading && rows.length === 0 ? (
          <p className="py-4 text-sm text-gray-500 text-center">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-sm text-gray-500 text-center">No members yet.</p>
        ) : (
          rows.map((r) => (
            <ActivityRow key={r.user_id} row={r} />
          ))
        )}
      </div>

      {/* Totals — always shown for quick scan. */}
      {rows.length > 1 && (
        <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center pt-3 mt-1 border-t border-gray-100 text-sm">
          <div className="font-semibold text-gray-800">Total</div>
          <div className="w-16 text-right font-semibold text-emerald-700">{totals.completed_count}</div>
          <div className="w-16 text-right font-semibold text-indigo-700">{totals.created_count}</div>
          <div className={`w-16 text-right font-semibold ${totals.overdue_count ? 'text-rose-600' : 'text-gray-400'}`}>
            {totals.overdue_count}
          </div>
        </div>
      )}
    </div>
  );
};

const ActivityRow = ({ row }) => {
  const initials = useMemo(() => initialsOf(row.name), [row.name]);
  return (
    <div
      className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2.5"
      data-testid={`team-activity-row-${row.user_id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate" title={row.name}>
            {row.name}
            {row.role !== 'member' && (
              <span className="ml-1.5 text-[10px] uppercase tracking-wide font-semibold text-indigo-600">
                {row.role}
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500 truncate">{row.email}</p>
        </div>
      </div>
      <div className="w-16 text-right text-sm font-semibold text-emerald-700">
        {row.completed_count}
      </div>
      <div className="w-16 text-right text-sm font-semibold text-indigo-700">
        {row.created_count}
      </div>
      <div className={`w-16 text-right text-sm font-semibold ${row.overdue_count ? 'text-rose-600' : 'text-gray-400'}`}>
        {row.overdue_count}
      </div>
    </div>
  );
};

export default TeamActivityCard;
